import React, { useState, useEffect } from 'react';
import { HelpdeskService } from '@/services/helpdesk.service';
import {
  HelpdeskTicket,
  TicketStatus,
  TicketPriority,
  TicketType,
  TicketSource,
} from '@/models/helpdesk-ticket.model';
import { useAuth } from '@/contexts/AuthContext';

const TicketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.open]: 'Open',
  [TicketStatus.inProgress]: 'In Progress',
  [TicketStatus.waitingForCustomer]: 'Waiting for Customer',
  [TicketStatus.resolved]: 'Resolved',
  [TicketStatus.closed]: 'Closed',
};

const TicketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.low]: 'Low',
  [TicketPriority.medium]: 'Medium',
  [TicketPriority.high]: 'High',
  [TicketPriority.urgent]: 'Urgent',
};

const TicketTypeLabels: Record<TicketType, string> = {
  [TicketType.technical]: 'Technical',
  [TicketType.billing]: 'Billing',
  [TicketType.account]: 'Account',
  [TicketType.feature]: 'Feature',
  [TicketType.bug]: 'Bug',
  [TicketType.other]: 'Other',
};

export const Helpdesk: React.FC = () => {
  const { profile } = useAuth();
  const [tickets, setTickets] = useState<HelpdeskTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<HelpdeskTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<TicketStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<TicketPriority | 'all'>('all');
  const [filterType, setFilterType] = useState<TicketType | 'all'>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<HelpdeskTicket | null>(null);

  useEffect(() => {
    const unsubscribe = HelpdeskService.subscribeToTickets((data) => {
      setTickets(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...tickets];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.ticketNumber.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    if (filterPriority !== 'all') {
      filtered = filtered.filter(t => t.priority === filterPriority);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type === filterType);
    }

    setFilteredTickets(filtered);
  }, [tickets, searchQuery, filterStatus, filterPriority, filterType]);

  const handleViewTicket = async (ticket: HelpdeskTicket) => {
    const fullTicket = await HelpdeskService.getTicketById(ticket.id);
    if (fullTicket) {
      setSelectedTicket(fullTicket);
    }
  };

  const handleAddComment = async (ticketId: string, content: string, isInternal: boolean) => {
    if (!profile) return;
    
    try {
      await HelpdeskService.addComment(ticketId, {
        authorId: profile.id,
        authorName: profile.fullName,
        authorEmail: profile.email,
        isInternal,
        content,
      });
      
      // Reload ticket
      const updated = await HelpdeskService.getTicketById(ticketId);
      if (updated) setSelectedTicket(updated);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleAssignTicket = async (ticketId: string) => {
    if (!profile) return;
    
    try {
      await HelpdeskService.assignTicket(ticketId, profile.id, profile.fullName);
      // Reload tickets
      const updated = await HelpdeskService.getTicketById(ticketId);
      if (updated) setSelectedTicket(updated);
    } catch (error) {
      console.error('Error assigning ticket:', error);
      alert('Failed to assign ticket');
    }
  };

  const handleUpdateStatus = async (ticketId: string, status: TicketStatus) => {
    try {
      await HelpdeskService.updateTicket(ticketId, { status });
      const updated = await HelpdeskService.getTicketById(ticketId);
      if (updated) setSelectedTicket(updated);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update ticket status');
    }
  };

  // Calculate stats
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === TicketStatus.open).length,
    inProgress: tickets.filter(t => t.status === TicketStatus.inProgress).length,
    resolved: tickets.filter(t => t.status === TicketStatus.resolved).length,
    urgent: tickets.filter(t => t.priority === TicketPriority.urgent).length,
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Helpdesk</h1>
            <p className="mt-1 text-sm text-gray-500">Manage support tickets from members, employers, and providers</p>
          </div>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Ticket
          </button>
        </div>

        {/* Stats Cards */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard label="Total Tickets" value={stats.total} icon="🎫" color="blue" />
            <StatCard label="Open" value={stats.open} icon="📂" color="yellow" />
            <StatCard label="In Progress" value={stats.inProgress} icon="⚙️" color="blue" />
            <StatCard label="Resolved" value={stats.resolved} icon="✅" color="green" />
            <StatCard label="Urgent" value={stats.urgent} icon="🚨" color="red" />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tickets..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as TicketStatus | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                {Object.entries(TicketStatusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value as TicketPriority | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priorities</option>
                {Object.entries(TicketPriorityLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as TicketType | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                {Object.entries(TicketTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Ticket List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Tickets ({filteredTickets.length})
            </h3>
          </div>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredTickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  onView={() => handleViewTicket(ticket)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onAddComment={handleAddComment}
          onAssign={handleAssignTicket}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      {/* Create Ticket Dialog */}
      {showCreateDialog && (
        <CreateTicketDialog
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
          }}
        />
      )}
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'red';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    red: 'bg-red-50 text-red-600 border-red-200',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="text-3xl opacity-75">{icon}</div>
      </div>
    </div>
  );
};

interface TicketCardProps {
  ticket: HelpdeskTicket;
  onView: () => void;
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket, onView }) => {
  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case TicketPriority.urgent:
        return 'bg-red-100 text-red-800';
      case TicketPriority.high:
        return 'bg-orange-100 text-orange-800';
      case TicketPriority.medium:
        return 'bg-yellow-100 text-yellow-800';
      case TicketPriority.low:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.open:
        return 'bg-blue-100 text-blue-800';
      case TicketStatus.inProgress:
        return 'bg-purple-100 text-purple-800';
      case TicketStatus.resolved:
        return 'bg-green-100 text-green-800';
      case TicketStatus.closed:
        return 'bg-gray-100 text-gray-800';
      case TicketStatus.waitingForCustomer:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer" onClick={onView}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className="font-mono text-sm text-gray-500">{ticket.ticketNumber}</span>
            <h4 className="text-lg font-semibold text-gray-900">{ticket.title}</h4>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
              {TicketPriorityLabels[ticket.priority]}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
              {TicketStatusLabels[ticket.status]}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600 line-clamp-2">{ticket.description}</p>
          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
            <span>{TicketTypeLabels[ticket.type]}</span>
            <span>•</span>
            <span>Created by {ticket.createdByName || ticket.createdByEmail || 'Unknown'}</span>
            {ticket.assignedToName && (
              <>
                <span>•</span>
                <span>Assigned to {ticket.assignedToName}</span>
              </>
            )}
            <span>•</span>
            <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TicketDetailModalProps {
  ticket: HelpdeskTicket;
  onClose: () => void;
  onAddComment: (ticketId: string, content: string, isInternal: boolean) => void;
  onAssign: (ticketId: string) => void;
  onUpdateStatus: (ticketId: string, status: TicketStatus) => void;
}

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
  ticket,
  onClose,
  onAddComment,
  onAssign,
  onUpdateStatus,
}) => {
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  const handleSubmitComment = () => {
    if (!comment.trim()) return;
    onAddComment(ticket.id, comment, isInternal);
    setComment('');
    setIsInternal(false);
    setShowCommentForm(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{ticket.title}</h2>
              <p className="text-sm text-gray-500 mt-1">{ticket.ticketNumber}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            </div>

            <div className="mb-6 border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Comments</h3>
              <div className="space-y-4">
                {ticket.comments.map((comment) => (
                  <div key={comment.id} className={`p-4 rounded-lg ${comment.isInternal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm">{comment.authorName}</span>
                      {comment.isInternal && (
                        <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">Internal</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{comment.content}</p>
                    <p className="text-xs text-gray-500 mt-2">{new Date(comment.createdAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>

              {!showCommentForm ? (
                <button
                  onClick={() => setShowCommentForm(true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Comment
                </button>
              ) : (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-600">Internal note (staff only)</span>
                    </label>
                    <div className="space-x-2">
                      <button
                        onClick={() => {
                          setShowCommentForm(false);
                          setComment('');
                        }}
                        className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitComment}
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-6 flex items-center justify-between">
              <div className="flex space-x-2">
                {!ticket.assignedTo && (
                  <button
                    onClick={() => onAssign(ticket.id)}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Assign to Me
                  </button>
                )}
                <select
                  value={ticket.status}
                  onChange={(e) => onUpdateStatus(ticket.id, e.target.value as TicketStatus)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                >
                  {Object.entries(TicketStatusLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CreateTicketDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateTicketDialog: React.FC<CreateTicketDialogProps> = ({ onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<TicketType>(TicketType.other);
  const [priority, setPriority] = useState<TicketPriority>(TicketPriority.medium);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await HelpdeskService.createTicket({
        title: title.trim(),
        description: description.trim(),
        type,
        priority,
        source: TicketSource.internal,
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Ticket</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as TicketType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                >
                  {Object.entries(TicketTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TicketPriority)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
                >
                  {Object.entries(TicketPriorityLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

